/**
 * spOidc.js — SocialPulse OIDC client (PKCE, no client_secret).
 *
 * Flow:
 *   1. `beginSocialPulseLogin(next)` builds PKCE verifier + state,
 *       stashes them in sessionStorage, then redirects the browser to
 *       the issuer's `/authorize` endpoint.
 *   2. Issuer bounces back to `/auth/socialpulse-callback?code=…&state=…`.
 *   3. Callback page exchanges code → id_token at `${issuer}/token`
 *       with the stored code_verifier, then POSTs the id_token to
 *       our own backend `/api/auth/oidc-session` which mints a local
 *       FoodPulse session cookie.
 */

const ISSUER    = process.env.REACT_APP_SOCIALPULSE_OIDC_ISSUER;
const CLIENT_ID = process.env.REACT_APP_SOCIALPULSE_OIDC_CLIENT_ID;
const CALLBACK_PATH = "/auth/socialpulse-callback";
const STORAGE_STATE   = "sp_oidc_state";
const STORAGE_VERIFIER = "sp_oidc_verifier";
const STORAGE_NEXT    = "sp_oidc_next";
const STORAGE_ENDPOINTS = "sp_oidc_endpoints";  // cached discovery doc

export const socialPulseConfigured = () => !!(ISSUER && CLIENT_ID);

/** Fetch the OP's discovery doc and return its advertised endpoints.
 *  Falls back to /authorize + /token relative to the issuer for OPs
 *  that don't publish a discovery doc. */
async function loadDiscovery() {
  if (!ISSUER) throw new Error("SocialPulse SSO not configured");
  const cached = sessionStorage.getItem(STORAGE_ENDPOINTS);
  if (cached) {
    try { return JSON.parse(cached); } catch { /* fall through */ }
  }
  const discoveryUrl = ISSUER.replace(/\/+$/, "") + "/.well-known/openid-configuration";
  try {
    const r = await fetch(discoveryUrl);
    if (r.ok) {
      const doc = await r.json();
      const endpoints = {
        authorize: doc.authorization_endpoint,
        token:     doc.token_endpoint,
      };
      if (endpoints.authorize && endpoints.token) {
        sessionStorage.setItem(STORAGE_ENDPOINTS, JSON.stringify(endpoints));
        return endpoints;
      }
    }
  } catch { /* fall through to hardcoded fallback */ }
  return {
    authorize: ISSUER.replace(/\/+$/, "") + "/authorize",
    token:     ISSUER.replace(/\/+$/, "") + "/token",
  };
}

function base64UrlEncode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function sha256(input) {
  const enc = new TextEncoder().encode(input);
  return crypto.subtle.digest("SHA-256", enc);
}

function randomString(bytes = 32) {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return base64UrlEncode(arr);
}

export async function beginSocialPulseLogin(next = "/") {
  if (!socialPulseConfigured()) throw new Error("SocialPulse SSO not configured");
  const state    = randomString(24);
  const verifier = randomString(32);
  const challenge = base64UrlEncode(await sha256(verifier));

  sessionStorage.setItem(STORAGE_STATE, state);
  sessionStorage.setItem(STORAGE_VERIFIER, verifier);
  sessionStorage.setItem(STORAGE_NEXT, next || "/");

  const redirectUri = window.location.origin + CALLBACK_PATH;
  const endpoints = await loadDiscovery();
  const url = new URL(endpoints.authorize);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id",     CLIENT_ID);
  url.searchParams.set("redirect_uri",  redirectUri);
  url.searchParams.set("scope",         "openid email profile");
  url.searchParams.set("state",         state);
  url.searchParams.set("code_challenge",        challenge);
  url.searchParams.set("code_challenge_method", "S256");
  window.location.assign(url.toString());
}

export async function completeSocialPulseCallback() {
  if (!socialPulseConfigured()) throw new Error("SocialPulse SSO not configured");
  const q = new URLSearchParams(window.location.search);
  const code  = q.get("code");
  const state = q.get("state");
  if (!code || !state) throw new Error("Missing code or state in callback URL");
  const savedState = sessionStorage.getItem(STORAGE_STATE);
  const verifier   = sessionStorage.getItem(STORAGE_VERIFIER);
  const next       = sessionStorage.getItem(STORAGE_NEXT) || "/";
  if (state !== savedState) throw new Error("State mismatch — please retry sign-in");
  if (!verifier) throw new Error("PKCE verifier missing — please retry sign-in");

  const redirectUri = window.location.origin + CALLBACK_PATH;
  const endpoints = await loadDiscovery();
  const tokenUrl = endpoints.token;
  const body = new URLSearchParams({
    grant_type:    "authorization_code",
    code,
    redirect_uri:  redirectUri,
    client_id:     CLIENT_ID,
    code_verifier: verifier,
  });
  const tr = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!tr.ok) {
    const txt = await tr.text().catch(() => "");
    throw new Error(`Token exchange failed (${tr.status}): ${txt.slice(0, 200)}`);
  }
  const json = await tr.json();
  const id_token = json.id_token;
  if (!id_token) throw new Error("Token response missing id_token");
  const access_token = json.access_token || null;

  // Cleanup PKCE artefacts.
  sessionStorage.removeItem(STORAGE_STATE);
  sessionStorage.removeItem(STORAGE_VERIFIER);
  sessionStorage.removeItem(STORAGE_NEXT);
  sessionStorage.removeItem(STORAGE_ENDPOINTS);
  return { id_token, access_token, next };
}
